<template>
  <Teleport to="body">
    <Transition name="modal-fade">
      <div
        v-if="isVisible"
        class="modal-overlay"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        @mousedown.self="cancel"
      >
        <Transition name="modal-scale" appear>
          <div
            v-if="isVisible"
            class="modal-content"
          >
            <div class="modal-icon-wrapper">
              <div class="modal-icon-glow"></div>

              <img
                :src="iconSrc"
                class="modal-icon"
                alt=""
              />
            </div>

            <div class="modal-copy">
              <h2
                :id="titleId"
                class="modal-title"
              >
                {{ titleText }}
              </h2>

              <p class="modal-text">
                {{ messageText }}
              </p>
            </div>

            <div class="modal-buttons">
              <button
                type="button"
                class="modal-button cancel-button"
                @click="cancel"
              >
                {{ cancelText }}
              </button>

              <button
                type="button"
                class="modal-button confirm-button"
                @click="confirmExit"
              >
                <span>
                  {{ confirmText }}
                </span>

                <svg
                  class="confirm-arrow"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M9 6L15 12L9 18"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import {
  defineEmits,
  defineProps,
} from "vue";

import warningIcon from "../assets/warning-icon.png";

const titleId = "registration-confirm-title";

const props = defineProps({
  isVisible: {
    type: Boolean,
    default: false,
  },

  titleText: {
    type: String,
    default: "Прервать регистрацию?",
  },

  messageText: {
    type: String,
    default:
      "Если вы выйдете сейчас, все несохранённые данные будут утеряны. Вы уверены, что хотите прервать регистрацию?",
  },

  cancelText: {
    type: String,
    default: "Остаться",
  },

  confirmText: {
    type: String,
    default: "Выйти",
  },

  iconSrc: {
    type: String,
    default: () => warningIcon,
  },
});

const emit = defineEmits([
  "close",
  "confirm",
]);

const cancel = () => {
  emit("close");
};

const confirmExit = () => {
  emit("confirm");
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;

  z-index: 999999;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 24px;

  background:
    rgba(6, 10, 18, 0.72);

  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
}

.modal-content {
  --modal-bg:
    linear-gradient(
      145deg,
      rgba(35, 39, 48, 0.98),
      rgba(24, 27, 34, 0.98)
    );

  --modal-border:
    rgba(255, 255, 255, 0.1);

  --modal-title:
    rgba(255, 255, 255, 0.98);

  --modal-text:
    rgba(255, 255, 255, 0.58);

  --secondary-bg:
    rgba(255, 255, 255, 0.07);

  --secondary-bg-hover:
    rgba(255, 255, 255, 0.11);

  width: min(
    100%,
    420px
  );

  position: relative;

  overflow: hidden;

  padding:
    30px
    30px
    26px;

  text-align: center;

  background: var(--modal-bg);

  border:
    1px solid
    var(--modal-border);

  border-radius: 26px;

  box-shadow:
    0 35px 90px
      rgba(0, 0, 0, 0.5),
    0 10px 35px
      rgba(0, 0, 0, 0.25),
    inset 0 1px 0
      rgba(255, 255, 255, 0.06);
}

.modal-content::before {
  content: "";

  position: absolute;

  width: 220px;
  height: 220px;

  top: -150px;
  left: 50%;

  transform:
    translateX(-50%);

  background:
    radial-gradient(
      circle,
      rgba(255, 74, 74, 0.16),
      transparent 68%
    );

  pointer-events: none;
}

[data-theme="light"] .modal-content {
  --modal-bg:
    linear-gradient(
      145deg,
      rgba(255, 255, 255, 0.99),
      rgba(248, 250, 253, 0.99)
    );

  --modal-border:
    rgba(20, 34, 55, 0.1);

  --modal-title:
    #172033;

  --modal-text:
    #6d7686;

  --secondary-bg:
    rgba(20, 34, 55, 0.055);

  --secondary-bg-hover:
    rgba(20, 34, 55, 0.09);

  box-shadow:
    0 35px 90px
      rgba(35, 50, 75, 0.18),
    0 10px 35px
      rgba(35, 50, 75, 0.08),
    inset 0 1px 0
      rgba(255, 255, 255, 0.9);
}

/* Icon */

.modal-icon-wrapper {
  width: 74px;
  height: 74px;

  position: relative;

  display: flex;
  align-items: center;
  justify-content: center;

  margin:
    0 auto
    22px;
}

.modal-icon-glow {
  position: absolute;
  inset: 0;

  background:
    radial-gradient(
      circle,
      rgba(229, 57, 53, 0.23),
      rgba(229, 57, 53, 0.06) 55%,
      transparent 72%
    );

  border-radius: 50%;
}

.modal-icon-wrapper::before {
  content: "";

  position: absolute;

  inset: 7px;

  background:
    linear-gradient(
      145deg,
      rgba(229, 57, 53, 0.18),
      rgba(198, 40, 40, 0.08)
    );

  border:
    1px solid
    rgba(229, 57, 53, 0.22);

  border-radius: 21px;

  transform:
    rotate(5deg);
}

.modal-icon {
  width: 42px;
  height: 42px;

  position: relative;

  z-index: 1;

  object-fit: contain;
}

/* Copy */

.modal-copy {
  max-width: 350px;

  margin:
    0 auto
    26px;
}

.modal-title {
  margin:
    0 0
    11px;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 22px;
  line-height: 1.25;

  letter-spacing:
    -0.35px;

  color:
    var(--modal-title);
}

.modal-text {
  margin: 0;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 13px;
  line-height: 1.6;

  color:
    var(--modal-text);
}

/* Buttons */

.modal-buttons {
  display: grid;

  grid-template-columns:
    1fr
    1fr;

  gap: 10px;
}

.modal-button {
  min-height: 48px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  gap: 8px;

  padding:
    0
    18px;

  border: none;

  border-radius: 14px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 13px;

  cursor: pointer;

  transition:
    transform 0.18s ease,
    background 0.18s ease,
    box-shadow 0.18s ease,
    opacity 0.18s ease;
}

.cancel-button {
  color:
    var(--modal-title);

  background:
    var(--secondary-bg);

  border:
    1px solid
    var(--modal-border);
}

.cancel-button:hover {
  background:
    var(--secondary-bg-hover);

  transform:
    translateY(-1px);
}

.confirm-button {
  color: #ffffff;

  background:
    linear-gradient(
      135deg,
      #e53935,
      #c62828
    );

  box-shadow:
    0 10px 26px
      rgba(229, 57, 53, 0.26);
}

.confirm-button:hover {
  transform:
    translateY(-1px);

  box-shadow:
    0 14px 32px
      rgba(229, 57, 53, 0.34);
}

.modal-button:active {
  transform:
    translateY(1px)
    scale(0.985);
}

.confirm-arrow {
  width: 16px;
  height: 16px;

  transition:
    transform 0.18s ease;
}

.confirm-button:hover
.confirm-arrow {
  transform:
    translateX(3px);
}

/* Animations */

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition:
    opacity 0.22s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-scale-enter-active {
  transition:
    opacity 0.25s ease,
    transform
      0.28s
      cubic-bezier(
        0.22,
        1,
        0.36,
        1
      );
}

.modal-scale-leave-active {
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}

.modal-scale-enter-from {
  opacity: 0;

  transform:
    translateY(10px)
    scale(0.96);
}

.modal-scale-leave-to {
  opacity: 0;

  transform:
    translateY(5px)
    scale(0.98);
}

/* Mobile */

@media (max-width: 768px) {
  .modal-overlay {
    padding: 18px;
  }

  .modal-content {
    padding:
      26px
      20px
      20px;

    border-radius: 22px;
  }

  .modal-icon-wrapper {
    width: 66px;
    height: 66px;

    margin-bottom: 18px;
  }

  .modal-icon {
    width: 38px;
    height: 38px;
  }

  .modal-title {
    font-size: 20px;
  }

  .modal-text {
    font-size: 12px;
  }

  .modal-buttons {
    grid-template-columns: 1fr;
  }

  .modal-button {
    width: 100%;
    min-height: 50px;
  }

  .confirm-button {
    order: -1;
  }
}

@media (
  prefers-reduced-motion:
  reduce
) {
  .modal-fade-enter-active,
  .modal-fade-leave-active,
  .modal-scale-enter-active,
  .modal-scale-leave-active,
  .modal-button,
  .confirm-arrow {
    transition-duration:
      0.01ms !important;
  }
}
</style>